"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import {
  Briefcase,
  Users,
  FileText,
  Receipt,
  Clock,
  Loader2,
  DollarSign,
  RefreshCw,
  Tag,
  HelpCircle,
  Search,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/hooks/useTranslations";
import type { SearchResponse, SearchResult } from "@/types/search";

interface SearchCommandProps {
  // No longer needs open/onOpenChange props
}

const RECENT_SEARCHES_KEY = "jobwright-recent-searches";
const MAX_RECENT_SEARCHES = 5;

// Icon mapping for different result types
const typeIcons = {
  job: Briefcase,
  client: Users,
  quote: FileText,
  invoice: Receipt,
  priceBookItem: Tag,
  serviceAgreement: RefreshCw,
  helpArticle: HelpCircle,
};

// Badge color mapping for statuses
const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  // Job statuses
  PENDING: "secondary",
  IN_PROGRESS: "default",
  COMPLETED: "outline",
  CANCELLED: "destructive",
  ON_HOLD: "secondary",
  // Quote statuses
  DRAFT: "secondary",
  SENT: "default",
  ACCEPTED: "outline",
  REJECTED: "destructive",
  EXPIRED: "destructive",
  // Invoice statuses
  PAID: "outline",
  OVERDUE: "destructive",
  VIEWED: "default",
  // Service Agreement statuses
  ACTIVE: "default",
  PAUSED: "secondary",
};

export function SearchCommand({}: SearchCommandProps) {
  const router = useRouter();
  const t = useTranslations('search');
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [results, setResults] = React.useState<SearchResponse | null>(null);
  const [recentSearches, setRecentSearches] = React.useState<string[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Load recent searches from localStorage on mount
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        try {
          setRecentSearches(JSON.parse(stored));
        } catch (e) {
          console.error("Failed to parse recent searches:", e);
        }
      }
    }
  }, []);

  // Save recent search
  const saveRecentSearch = (search: string) => {
    if (typeof window !== "undefined") {
      const updated = [
        search,
        ...recentSearches.filter((s) => s !== search),
      ].slice(0, MAX_RECENT_SEARCHES);
      setRecentSearches(updated);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    }
  };

  // Debounced search
  React.useEffect(() => {
    if (query.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(query)}`
        );
        if (response.ok) {
          const data = await response.json();
          setResults(data);
        } else {
          console.error("Search failed with status:", response.status);
        }
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [query]);

  // Handle selecting a result
  const handleSelect = (result: SearchResult) => {
    saveRecentSearch(query);
    setIsOpen(false);
    setQuery("");
    router.push(result.url);
  };

  // Handle selecting a recent search
  const handleRecentSearch = (search: string) => {
    setQuery(search);
    inputRef.current?.focus();
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard shortcut (Cmd+K or Ctrl+K)
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Flatten all results for keyboard navigation
  const allResults = React.useMemo(() => {
    if (!results) return [];
    return [
      ...results.jobs,
      ...results.clients,
      ...results.quotes,
      ...results.invoices,
      ...results.priceBookItems,
      ...results.serviceAgreements,
      ...results.helpArticles,
    ];
  }, [results]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Clear search
  const handleClear = () => {
    setQuery("");
    setResults(null);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Format status for display
  const formatStatus = (status: string) => {
    return status
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Render a search result item
  const renderResultItem = (result: SearchResult, Icon: any) => {
    const isHelpArticle = result.type === "helpArticle";
    return (
      <button
        key={result.id}
        onClick={() => handleSelect(result)}
        className={`flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer ${
          isHelpArticle ? "bg-blue-50/50 dark:bg-blue-950/20 border-l-2 border-blue-400" : ""
        }`}
      >
        <Icon className={`h-4 w-4 ${isHelpArticle ? "text-blue-600 dark:text-blue-400" : "text-gray-500"}`} />
        <div className="flex-1 min-w-0">
          <div className={`font-medium truncate text-sm ${isHelpArticle ? "text-blue-900 dark:text-blue-100" : "text-gray-900 dark:text-gray-100"}`}>
            {result.title}
          </div>
          {result.subtitle && (
            <div className={`text-xs truncate ${isHelpArticle ? "text-blue-700 dark:text-blue-300" : "text-gray-500"}`}>
              {result.subtitle}
            </div>
          )}
        </div>
        {result.metadata?.status && (
          <Badge
            variant={statusColors[result.metadata.status] || "default"}
            className="shrink-0 text-xs"
          >
            {formatStatus(result.metadata.status)}
          </Badge>
        )}
        {result.metadata?.price && (
          <div className="shrink-0 flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
            <DollarSign className="h-3 w-3" />
            {result.metadata.price.toFixed(2)}
          </div>
        )}
        {result.metadata?.category && result.type === "helpArticle" && (
          <Badge variant="outline" className="shrink-0 text-xs border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300">
            {result.metadata.category}
          </Badge>
        )}
      </button>
    );
  };

  return (
    <div className="relative w-full">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={t('placeholder')}
          className="w-full pl-10 pr-20 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {loading && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          )}
          {query && !loading && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-6 w-6 p-0"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <kbd className="hidden sm:inline-flex h-6 select-none items-center gap-1 rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-2 font-mono text-xs font-medium text-gray-600 dark:text-gray-400">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Dropdown Results */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-[500px] overflow-y-auto z-50"
        >
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">{t('searching')}</span>
            </div>
          )}

          {/* Recent Searches */}
          {!loading && query.length < 2 && recentSearches.length > 0 && (
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('recentSearches')}
              </div>
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentSearch(search)}
                  className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer rounded"
                >
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{search}</span>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {!loading && query.length >= 2 && results && results.total === 0 && (
            <div className="py-8 text-center text-sm text-gray-500">
              {t('noResults', { query })}
            </div>
          )}

          {/* Results */}
          {!loading && results && results.total > 0 && (
            <div className="p-2">
              {/* Jobs */}
              {results.jobs.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('categories.jobs')}
                  </div>
                  {results.jobs.map((job) => renderResultItem(job, typeIcons.job))}
                </div>
              )}

              {/* Clients */}
              {results.clients.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('categories.clients')}
                  </div>
                  {results.clients.map((client) => renderResultItem(client, typeIcons.client))}
                </div>
              )}

              {/* Quotes */}
              {results.quotes.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('categories.quotes')}
                  </div>
                  {results.quotes.map((quote) => renderResultItem(quote, typeIcons.quote))}
                </div>
              )}

              {/* Invoices */}
              {results.invoices.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('categories.invoices')}
                  </div>
                  {results.invoices.map((invoice) => renderResultItem(invoice, typeIcons.invoice))}
                </div>
              )}

              {/* Price Book Items */}
              {results.priceBookItems && results.priceBookItems.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('categories.priceBook')}
                  </div>
                  {results.priceBookItems.map((item) => renderResultItem(item, typeIcons.priceBookItem))}
                </div>
              )}

              {/* Service Agreements */}
              {results.serviceAgreements && results.serviceAgreements.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('categories.serviceAgreements')}
                  </div>
                  {results.serviceAgreements.map((agreement) => renderResultItem(agreement, typeIcons.serviceAgreement))}
                </div>
              )}

              {/* Help Articles */}
              {results.helpArticles && results.helpArticles.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('categories.helpDocs')}
                  </div>
                  {results.helpArticles.map((article) => renderResultItem(article, typeIcons.helpArticle))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
